/**
 * Générateur — Identifier les entrées et sorties d'un programme.
 * Attendu ID : 997 | Niveau : 5ème | Domaine : Pensée informatique (cluster 2, O2)
 *
 * Source : doc prof « TANOED EXERCISEUR la pensée informatique », cluster 2
 * (Ex.6, Ex.9, Créé n°64).
 *
 * Plan de cartes (validé 2026-07-12) :
 *
 *   Carte « assoc » — Ex.6 : instruction ↔ fonctionnalité ↔ nature. Pool
 *   commun de 10 instructions (table de vérité codée une fois : instruction,
 *   description, entrée/sortie). relier (4 instructions ↔ 4 descriptions —
 *   tirage avec ≥ 1 entrée et ≥ 1 sortie) + tableau colonnesLibres
 *   (Entrée/Sortie, jamais tout-même-colonne par le même tirage).
 *
 *   Carte « tri » — Ex.9 : classer (Entrées / Sorties, 3 + 3). Le piège
 *   central du doc est TOUJOURS présent : « dire « Quel est ton nom ? »
 *   pendant 2 secondes » (le lutin PARLE : sortie) tiré avec « demander …
 *   et attendre » (entrée) ; « touche espace pressée ? » = entrée (capteur).
 *
 *   Carte « saisi » — Créé n°64 (l'exemple exact du cadrage) : programme
 *   demander → dire « Bonjour ! ». qcm (a. l'entrée / la sortie — la demande
 *   alterne) + qcm (b. remplacer « Bonjour ! » par le bloc « réponse ») +
 *   texte_trou_saisie (c. avec la modification, taper N affiche N — la
 *   valeur naît à l'exécution) + qcm (d. le double → dire « réponse × 2 »,
 *   pont vers le cluster 3).
 *
 *   Invariants : toutes les natures entrée/sortie sont portées par la table
 *   de vérité du pool (relue une fois) ; descriptions deux à deux distinctes
 *   par construction (pool sans doublon, paramètres distincts injectés).
 */

import {
    randomChoice, randInt, shuffle, randomChoiceMultiple,
    makeQcm, makeTexteTrou, makeRelier, makeClasser, basePayload,
} from '../../../utils';
import { imgElement, svgScript } from './svgScratch';

const ATTENDU_ID = 997;

const L_ASSOC = 'Instruction, fonctionnalité, entrée ou sortie';
const L_TRI = 'Entrée ou sortie ? Le tri';
const L_SAISI = 'Afficher le nombre saisi';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Pool d'instructions (table de vérité unique) ────────────
// nature : 0 = entrée, 1 = sortie. Les paramètres (n, son, messages) sont
// injectés au tirage pour la variabilité SANS toucher à la nature.

const SONS = ['Meow', 'Pop', 'Boing'];

const POOL = [
    {
        id: 'dire_msg',
        instr: (p) => `dire « ${p.m} » pendant 2 secondes`,
        desc: (p) => `Le lutin dit « ${p.m} » pendant 2 secondes.`,
        nature: 1,
    },
    {
        id: 'penser',
        instr: () => 'penser à « Hmm... »',
        desc: () => 'Le lutin affiche une bulle de pensée.',
        nature: 1,
    },
    {
        id: 'dire_question',   // LE piège : le lutin pose la question sans rien attendre
        instr: () => 'dire « Quel est ton nom ? » pendant 2 secondes',
        desc: () => 'Le lutin affiche la question, sans attendre de réponse.',
        nature: 1,
    },
    {
        id: 'demander_nom',
        instr: () => 'demander « Quel est ton nom ? » et attendre',
        desc: () => "Le programme attend que l'utilisateur tape une réponse au clavier.",
        nature: 0,
    },
    {
        id: 'demander_age',
        instr: () => 'demander « Quel âge as-tu ? » et attendre',
        desc: () => "Le programme attend que l'utilisateur tape son âge au clavier.",
        nature: 0,
    },
    {
        id: 'jouer_son',
        instr: (p) => `jouer le son ${p.son}`,
        desc: (p) => `Le lutin joue le son ${p.son}.`,
        nature: 1,
    },
    {
        id: 'dessiner',
        instr: (p) => `stylo en position d'écriture, puis avancer de ${p.n} pas`,
        desc: (p) => `Le lutin dessine un segment de ${p.n} pas.`,
        nature: 1,
    },
    {
        id: 'touche',
        instr: () => 'touche espace pressée ?',
        desc: () => 'Le programme lit si une touche du clavier est enfoncée.',
        nature: 0,
    },
    {
        id: 'avancer',
        instr: (p) => `avancer de ${p.n2} pas`,
        desc: (p) => `Le lutin se déplace de ${p.n2} pas.`,
        nature: 1,
    },
];

const tirerParams = () => ({
    m: randomChoice(['Bonjour !', 'Salut !', 'Coucou !', 'Bonne journée !']),
    son: randomChoice(SONS),
    n: randInt(1, 5) * 10,
    n2: randInt(1, 9) * 5,
});

/** k instructions distinctes avec au moins une entrée ET une sortie. */
const tirerInstructions = (k) => {
    const entrees = POOL.filter((e) => e.nature === 0);
    const sorties = POOL.filter((e) => e.nature === 1);
    const base = [randomChoice(entrees), randomChoice(sorties)];
    const reste = shuffle(POOL.filter((e) => !base.includes(e))).slice(0, k - 2);
    return shuffle([...base, ...reste]);
};

// ═════════ Carte 1 — Instruction ↔ fonctionnalité ↔ nature (Ex.6) ═════════

const genAssocRelier = () => {
    const p = tirerParams();
    const tirage = tirerInstructions(4);
    const pairs = tirage.map((e) => ({ source: e.instr(p), target: e.desc(p) }));
    const texte = '<p><b>Relie chaque instruction à ce qu\'elle fait dans le programme.</b></p>';
    const payload = makeRelier(pairs, texte, opts(L_ASSOC));
    payload._meta.params = { ids: tirage.map((e) => e.id), ...p };
    return payload;
};

const genAssocTableau = () => {
    const p = tirerParams();
    const tirage = tirerInstructions(4);
    const colonnes = ['Entrée', 'Sortie'];
    const lignes = tirage.map((e) => e.instr(p));
    const bonnes = tirage.map((e) => e.nature);
    const tableEl = {
        type: 'table',
        config: { type: 'colonnesLibres', colonnes, lignes, bonnesReponses: bonnes },
    };
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnes[i]]}]`).join('');
    const payload = basePayload('qcm',
        '<p><b>Pour chaque instruction, coche s\'il s\'agit d\'une entrée ou d\'une '
        + 'sortie du programme.</b></p>'
        + '<p>Une entrée est une information donnée AU programme ; une sortie est '
        + 'ce que le programme produit.</p>',
        { choix: [], bonne_reponse: br },
        opts(L_ASSOC),
        { version: 1, elements: [tableEl] });
    payload._meta.params = { ids: tirage.map((e) => e.id), ...p };
    return payload;
};

// ═════════ Carte 2 — Entrée ou sortie ? Le tri (Ex.9) ═════════

const genTriClasser = () => {
    const p = tirerParams();
    // le duo piège est TOUJOURS présent : dire « Quel est ton nom ? » (sortie)
    // face à demander « Quel est ton nom ? » et attendre (entrée)
    const direQuestion = POOL.find((e) => e.id === 'dire_question');
    const autresSorties = shuffle(POOL.filter((e) => e.nature === 1 && e !== direQuestion)).slice(0, 2);
    const entrees = POOL.filter((e) => e.nature === 0);   // les 3 entrées du pool
    const sorties = [direQuestion, ...autresSorties];
    const payload = makeClasser(
        [
            { name: 'Entrée du programme', items: shuffle(entrees.map((e) => e.instr(p))) },
            { name: 'Sortie du programme', items: shuffle(sorties.map((e) => e.instr(p))) },
        ],
        '<p><b>Classe chaque instruction : entrée ou sortie du programme ?</b></p>'
        + '<p>Attention : un lutin qui POSE une question ne récupère aucune '
        + 'information — lis chaque bloc jusqu\'au bout.</p>',
        opts(L_TRI));
    payload._meta.params = { entrees: entrees.map((e) => e.id), sorties: sorties.map((e) => e.id), ...p };
    return payload;
};

// ═════════ Carte 3 — Afficher le nombre saisi (Créé n°64) ═════════

const QUESTIONS_SAISIE = [
    'Choisis un nombre !', 'Donne un nombre !', 'Tape un nombre !',
    'Quel est ton nombre préféré ?',
];
const MSGS_SAISI = ['Bonjour !', 'Salut !', 'Coucou !', 'Bonne journée !', 'Bienvenue !', 'Bonsoir !'];

const svgSaisi = (q, blocDire) => svgScript([
    { t: 'hat', txt: 'quand le drapeau est cliqué' },
    { t: 'sensing', txt: `demander « ${q} » et attendre` },
    { t: 'looks', txt: blocDire },
]);

const TEXTE_SAISI = (q, m) => '<p><b>Voici un programme :</b> quand le drapeau est cliqué, '
    + `le lutin demande « ${q} » et attend, puis il dit « ${m} ».</p>`;

const genSaisiQcm = () => {
    const q = randomChoice(QUESTIONS_SAISIE);
    const m = randomChoice(MSGS_SAISI);
    const demande = randomChoice(['entrée', 'sortie']);
    const texte = TEXTE_SAISI(q, m)
        + `<p><b>Quelle est ${demande === 'entrée' ? "l'entrée" : 'la sortie'} de ce programme ?</b></p>`;
    const choixEntree = "Le nombre tapé au clavier par l'utilisateur";
    const choixSortie = `Le message « ${m} » dit par le lutin`;
    const correct = demande === 'entrée' ? choixEntree : choixSortie;
    const candidats = [
        demande === 'entrée' ? choixSortie : choixEntree,   // la confusion entrée/sortie
        'Le bloc « demander » lui-même',                     // instruction ≠ donnée
        'Le lutin',                                          // le personnage ≠ donnée
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_SAISI), elements_additionnels: imgElement(svgSaisi(q, `dire « ${m} »`)) });
    payload._meta.params = { q, m, demande };
    return payload;
};

const genSaisiModifQcm = () => {
    const q = randomChoice(QUESTIONS_SAISIE);
    const m = randomChoice(MSGS_SAISI);
    const texte = TEXTE_SAISI(q, m)
        + `<p><b>On veut que le lutin affiche le nombre saisi à la place de « ${m} ». `
        + 'Quelle modification faut-il faire ?</b></p>';
    const correct = `Remplacer « ${m} » par le bloc « réponse » dans le bloc « dire »`;
    const candidats = [
        'Ajouter un deuxième bloc « demander » à la fin du programme',   // demander n'affiche rien
        `Remplacer le bloc « demander » par un bloc « dire « ${q} » »`,  // le programme n'aurait plus d'entrée
        'Changer le déclencheur « quand le drapeau est cliqué »',
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_SAISI), elements_additionnels: imgElement(svgSaisi(q, `dire « ${m} »`)) });
    payload._meta.params = { q, m };
    return payload;
};

const genSaisiTrous = () => {
    const q = randomChoice(QUESTIONS_SAISIE);
    const n1 = randInt(2, 49);
    let n2 = randInt(2, 49);
    if (n2 === n1) n2 += 1;   // repli déterministe : deux essais distincts
    const phrase = `S'il tape ${n1}, le lutin affiche {{${n1}}}. S'il tape ${n2}, il affiche {{${n2}}}.`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Dans ce programme, le lutin dit maintenant le bloc « réponse » : il '
        + "affiche ce que l'utilisateur a tapé.</b></p>",
        { ...opts(L_SAISI), mode: 'saisie', elements_additionnels: imgElement(svgSaisi(q, 'dire réponse')) });
    payload._meta.params = { n1, n2 };
    return payload;
};

const NOMS_MULTIPLES = {
    2: 'le double', 3: 'le triple', 4: 'le quadruple',
    5: 'cinq fois la valeur', 10: 'dix fois la valeur',
};

const genSaisiDoubleQcm = () => {
    const k = randomChoice([2, 3, 4, 5, 10]);
    // n ≥ 3 : k×n ≠ k+n pour tout k ≥ 2 (k×n = k+n n'a de solution entière
    // que n = 2, k = 2) — l'exemple ne rend jamais deux choix cohérents
    const n = randInt(3, 20);
    const texte = '<p><b>Le lutin demande un nombre et on veut qu\'il affiche '
        + `${NOMS_MULTIPLES[k]} du nombre saisi. Par exemple, si l'utilisateur tape ${n}, `
        + `le lutin devra dire ${k * n}. Que faut-il mettre dans le bloc « dire » ?</b></p>`;
    const correct = `le bloc « réponse × ${k} »`;
    const candidats = [
        `le bloc « réponse + ${k} »`,        // multiple lu « + k »
        `le nombre ${k * n}`,                 // l'exemple figé (ne marche que pour n)
        'le bloc « réponse »',                // le nombre saisi, sans le calcul
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_SAISI), elements_additionnels: imgElement(svgSaisi(randomChoice(QUESTIONS_SAISIE), 'dire réponse × …')) });
    payload._meta.params = { k, n };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    assoc_relier: genAssocRelier,
    assoc_tableau_vf: genAssocTableau,
    tri_classer: genTriClasser,
    saisi_qcm_unique: genSaisiQcm,
    saisi_modif_qcm_unique: genSaisiModifQcm,
    saisi_texte_trou_saisie: genSaisiTrous,
    saisi_double_qcm_unique: genSaisiDoubleQcm,
};

const FORMAT_LABELS = {
    relier: 'Relier', tableau_vf: 'Tableau', classer: 'Classer',
    qcm_unique: 'QCM', modif_qcm_unique: 'QCM modification',
    texte_trou_saisie: 'Saisie trous', double_qcm_unique: 'QCM double',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'assoc', label: L_ASSOC, formatsList: buildFormats('assoc') },
    { id: 'tri', label: L_TRI, formatsList: buildFormats('tri') },
    { id: 'saisi', label: L_SAISI, formatsList: buildFormats('saisi') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
